import React, { useEffect, useState } from "react";
import { Loader2, Check, Clock, Sparkles } from "lucide-react";
import api from "../../api";
import { toast } from "../../hooks/use-toast";
import { usePlan } from "../../context/PlanContext";

const feeLine = (p) => `${p.commission_pct}%${p.commission_fixed ? ` + ${Math.round(p.commission_fixed * 100)}p` : ""} per sale`;

export default function PlansTab() {
  const { data, reload } = usePlan();
  const [busy, setBusy] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("sub") === "success" && params.get("session_id")) {
      api.get(`/business/subscription/confirm?session_id=${params.get("session_id")}`)
        .then(() => { toast({ title: "Plan activated", description: "Your new features are ready." }); reload(); })
        .finally(() => window.history.replaceState({}, "", "/dashboard?tab=plans"));
    }
  }, [reload]);

  if (!data) return <Loader2 className="animate-spin text-[#2ec9c9]" size={32} />;
  const current = data.current;
  const plans = [...data.plans].sort((a, b) => a.monthly_fee - b.monthly_fee);
  const paidActive = ["active", "waived", "invoiced"].includes(data.subscription_status);

  const choose = async (p) => {
    if (p.monthly_fee <= 0 && !window.confirm(`Switch to ${p.name}? Your paid features will stop and any subscription will be cancelled.`)) return;
    setBusy(p.id);
    try {
      const { data: res } = await api.post("/business/plan", { plan_id: p.id, origin: window.location.origin });
      if (res.url) { window.location.href = res.url; return; }
      toast({ title: `You're now on ${p.name}` });
      reload();
    } catch (err) {
      toast({ title: "Could not change plan", description: err?.response?.data?.detail || "Try again", variant: "destructive" });
    } finally { setBusy(""); }
  };

  return (
    <div data-testid="plans-tab">
      <p className="text-sm text-[#5a6b80] mb-6">You're on <strong className="text-[#16243b]" data-testid="current-plan-name">{current.name}</strong>
        {current.monthly_fee > 0 && <> · subscription <span className="capitalize" data-testid="current-plan-status">{data.subscription_status}</span></>}.</p>
      <div className="grid lg:grid-cols-3 gap-5">
        {plans.map((p, i) => {
          const onPlan = p.id === current.id;
          const isCurrent = onPlan && (p.monthly_fee <= 0 || paidActive);
          const feats = data.plan_features[p.id] || [];
          const featured = i === plans.length - 1;
          return (
            <div key={p.id} data-testid={`plan-card-${p.id}`}
              className={`relative rounded-3xl p-7 flex flex-col ${featured ? "bg-[#16243b] text-white shadow-[0_24px_60px_rgba(20,30,55,0.25)]" : "bg-white text-[#16243b] shadow-[0_8px_24px_rgba(20,30,55,0.06)]"} ${isCurrent ? "ring-2 ring-[#2ec9c9]" : ""}`}>
              {onPlan && <span className={`absolute top-5 right-5 rounded-full text-white text-[11px] font-semibold px-2.5 py-1 ${isCurrent ? "bg-[#2ec9c9]" : "bg-amber-500"}`} data-testid={`plan-current-${p.id}`}>{isCurrent ? "Current plan" : "Current · payment needed"}</span>}
              <h3 className="text-xl font-bold">{p.name}</h3>
              <p className={`text-sm mt-1 ${featured ? "text-white/70" : "text-[#8a97a8]"}`}>{p.tagline}</p>
              <p className="mt-5"><span className="text-4xl font-bold">£{p.monthly_fee.toFixed(2)}</span><span className={featured ? "text-white/60" : "text-[#8a97a8]"}>/month</span></p>
              <p className={`text-sm font-medium mt-1 ${featured ? "text-[#2ec9c9]" : "text-[#1fb0b0]"}`} data-testid={`plan-fee-${p.id}`}>{feeLine(p)}</p>
              <ul className="mt-6 space-y-2 text-sm flex-1">
                {data.base_included.map((f) => <li key={f} className="flex gap-2"><Check size={16} className="text-[#2ec9c9] shrink-0 mt-0.5" /> {f}</li>)}
                {data.features.filter((f) => p.features.includes(f.key)).map((f) => (
                  <li key={f.key} className="flex gap-2">
                    {feats.includes(f.key) ? <Check size={16} className="text-[#2ec9c9] shrink-0 mt-0.5" /> : <Clock size={16} className="text-amber-400 shrink-0 mt-0.5" />}
                    <span>{f.label}{f.coming_soon && <span className="ml-1.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-semibold px-1.5 py-0.5">Coming soon</span>}</span>
                  </li>
                ))}
              </ul>
              <button onClick={() => choose(p)} disabled={isCurrent || !!busy} data-testid={`choose-plan-${p.id}`}
                className={`mt-7 rounded-xl py-3 font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-60 ${featured ? "bg-[#2ec9c9] text-white" : "gp-btn-outline"}`}>
                {busy === p.id ? <Loader2 size={16} className="animate-spin" /> : !isCurrent && p.monthly_fee > current.monthly_fee ? <Sparkles size={15} /> : null}
                {isCurrent ? "Your plan" : onPlan ? `Activate ${p.name}` : p.monthly_fee > current.monthly_fee ? `Upgrade to ${p.name}` : `Switch to ${p.name}`}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
